import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderAPI } from '../services/api';
import { Order } from '../types';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';
import { getImageUrl } from '../utils/imageUrl';
import { useWebsiteSettings } from '../context/WebsiteSettingsContext';
import './OrderConfirmation.css';

const OrderConfirmation: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { t, i18n } = useTranslation();
  const { settings } = useWebsiteSettings();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const isBn = i18n.language === 'bn';
  const supportPhone = settings?.support_phone || '+880 1741-818807';
  const whatsappNumber = settings?.whatsapp_number || '8801741818807';

  useEffect(() => {
    // Clear any previous checkout preorder ID and direct order data
    try {
      sessionStorage.removeItem('checkout_preorder_id');
      sessionStorage.removeItem('directOrder');
    } catch {}

    const loadOrder = async () => {
      try {
        const data = await orderAPI.trackOrder(orderNumber!);
        setOrder(data);

        // Track Purchase if not already tracked for this order
        const trackedOrders = JSON.parse(localStorage.getItem('trackedOrders') || '[]');
        if (data && !trackedOrders.includes(data.order_number)) {
          // Extract product IDs - handle different API response formats
          const contentIds = data.items.map((item: any) => {
            const productId = item.product_id || item.product || item.id;
            return productId?.toString() || '';
          }).filter(Boolean);

          const totalValue = parseFloat(String(data.final_amount || data.total_amount || 0));
          const numItems = data.items.reduce((acc: number, item: any) => acc + (item.quantity || 1), 0);

          // Track Facebook & TikTok Purchase event
          await trackFacebookEvent('Purchase', {
            content_id: contentIds[0] || '',
            content_ids: contentIds,
            content_type: 'product',
            value: totalValue,
            currency: 'BDT',
            num_items: numItems,
            order_id: data.order_number
          });

          // Track GTM/GA4 Purchase event
          gtmEvents.purchase({
            orderId: data.order_number || '',
            totalValue: totalValue,
            shipping: Number(data.shipping_cost || 0),
            items: data.items.map((item: any) => ({
              id: item.product_id || item.product || item.id,
              name: item.product_name || item.name || '',
              price: parseFloat(String(item.price || 0)),
              quantity: item.quantity || 1,
            })),
          });

          console.log('Purchase events tracked (FB + GTM):', {
            order_id: data.order_number,
            value: totalValue,
            content_ids: contentIds,
            num_items: numItems
          });

          trackedOrders.push(data.order_number);
          localStorage.setItem('trackedOrders', JSON.stringify(trackedOrders));
        }
      } catch (error) {
        console.error('Failed to load order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (orderNumber) {
      loadOrder();
    }
  }, [orderNumber]);

  const handleCopyOrderNumber = () => {
    if (order?.order_number) {
      navigator.clipboard.writeText(order.order_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="order-confirmation-page oc-state-screen">
        <div className="oc-loader-card">
          <div className="oc-spinner"></div>
          <p>{t('common.loading', 'অর্ডার লোড হচ্ছে...')}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-confirmation-page oc-state-screen">
        <div className="oc-loader-card oc-error-card">
          <div className="oc-error-icon">✕</div>
          <h2>{t('common.error', 'অর্ডার পাওয়া যায়নি')}</h2>
          <p>আপনার অর্ডার নম্বরটি সঠিক কিনা পরীক্ষা করুন।</p>
          <Link to="/" className="btn-oc-continue">
            হোমপেজে ফিরে যান
          </Link>
        </div>
      </div>
    );
  }

  // Numerical Calculations
  const shippingCost = Number(order.shipping_cost || 0);
  const totalAmount = Number(order.final_amount || order.total_amount || 0);
  const discountAmount = Number(order.discount_amount || 0);
  const itemsSubtotal = order.items && order.items.length > 0
    ? order.items.reduce((sum, item) => sum + Number(item.subtotal || (Number(item.price || 0) * Number(item.quantity || 1))), 0)
    : Math.max(0, totalAmount - shippingCost);

  const orderDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString(isBn ? 'bn-BD' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="order-confirmation-page">
      <div className="order-confirmation-container">
        
        {/* Compact Success Header */}
        <header className="oc-header">
          <div className="oc-header-content">
            <div className="oc-check-circle" title="Success">
              <svg className="oc-check-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            
            <div className="oc-header-text">
              <div className="oc-title-row">
                <h1 className="oc-title">{t('orderConfirmation.title', 'অর্ডার নিশ্চিত করা হয়েছে!')}</h1>
                <span className="oc-pill-delivery">
                  <span className="oc-pulse-dot"></span>
                  {t('orderConfirmation.estimatedDelivery', 'আনুমানিক ডেলিভারি: ৩-৫ কার্যদিবস')}
                </span>
              </div>
              <p className="oc-subtitle">
                {isBn 
                  ? 'ধন্যবাদ! আপনার অর্ডারটি সফলভাবে গ্রহণ করা হয়েছে। দ্রুততম সময়ে আপনার ঠিকানায় পৌঁছে দেওয়া হবে।'
                  : 'Thank you! Your order has been placed successfully and will be delivered shortly.'}
              </p>
            </div>
          </div>
        </header>

        {/* 2-Column Responsive Dashboard */}
        <div className="oc-dashboard-grid">
          
          {/* LEFT COLUMN: Order Details, Shipping & Actions */}
          <div className="oc-col-details">
            
            {/* 4 Balanced Metric Cards in 2x2 Grid */}
            <div className="oc-info-cards-grid">
              
              {/* Card 1: Order Number */}
              <div className="oc-info-card card-order-id">
                <div className="oc-card-icon icon-emerald">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <div className="oc-card-body">
                  <span className="oc-card-label">{t('orderConfirmation.orderNumber', 'অর্ডার নম্বর')}</span>
                  <div className="oc-order-number-wrapper">
                    <span className="oc-order-number">{order.order_number}</span>
                    <button 
                      type="button" 
                      className={`btn-copy-order ${copied ? 'copied' : ''}`}
                      onClick={handleCopyOrderNumber}
                      title="Copy order ID"
                    >
                      {copied ? (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{isBn ? 'কপি' : 'Copied'}</span>
                        </>
                      ) : (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          <span>{isBn ? 'কপি' : 'Copy'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Card 2: Order Date */}
              <div className="oc-info-card">
                <div className="oc-card-icon icon-blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div className="oc-card-body">
                  <span className="oc-card-label">{t('orderConfirmation.orderDate', 'অর্ডার তারিখ')}</span>
                  <span className="oc-card-val date-val">{orderDate}</span>
                </div>
              </div>

              {/* Card 3: Payment Method */}
              <div className="oc-info-card">
                <div className="oc-card-icon icon-purple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div className="oc-card-body">
                  <span className="oc-card-label">পেমেন্ট পদ্ধতি</span>
                  <span className="oc-card-val badge-cod">{isBn ? 'ক্যাশ অন ডেলিভারি (COD)' : 'Cash on Delivery'}</span>
                </div>
              </div>

              {/* Card 4: Order Status */}
              <div className="oc-info-card">
                <div className="oc-card-icon icon-amber">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                </div>
                <div className="oc-card-body">
                  <span className="oc-card-label">অর্ডার স্ট্যাটাস</span>
                  <span className="oc-card-val badge-status">
                    {order.status_display || (isBn ? 'নিশ্চিত করা হয়েছে' : 'Confirmed')}
                  </span>
                </div>
              </div>

            </div>

            {/* Customer & Shipping Section Card */}
            <div className="oc-panel-card oc-shipping-card">
              <div className="oc-panel-header">
                <span className="oc-panel-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {t('orderConfirmation.shippingAddress', 'শিপিং ও গ্রাহকের তথ্য')}
                </span>
              </div>

              <div className="oc-shipping-content-grid">
                <div className="oc-shipping-field address-field">
                  <span className="field-title">{isBn ? 'ডেলিভারি ঠিকানা' : 'Delivery Address'}</span>
                  <p className="field-value address-text">
                    {order.shipping_address || (isBn ? 'ঠিকানা প্রদান করা হয়নি' : 'No address provided')}
                  </p>
                </div>

                <div className="oc-shipping-meta-row">
                  {order.customer_name && (
                    <div className="oc-shipping-field">
                      <span className="field-title">{isBn ? 'গ্রাহকের নাম' : 'Customer Name'}</span>
                      <p className="field-value font-semibold">{order.customer_name}</p>
                    </div>
                  )}
                  {order.customer_phone && (
                    <div className="oc-shipping-field">
                      <span className="field-title">{isBn ? 'ফোন নম্বর' : 'Phone Number'}</span>
                      <p className="field-value font-mono">{order.customer_phone}</p>
                    </div>
                  )}
                </div>

                {order.notes && (
                  <div className="oc-shipping-field notes-field">
                    <span className="field-title">{isBn ? 'অর্ডার নোট' : 'Order Notes'}</span>
                    <p className="field-value notes-text">{order.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="oc-actions-row">
              <Link to="/products" className="btn-oc-continue">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                <span>{isBn ? 'আরও কেনাকাটা করুন' : 'Continue Shopping'}</span>
              </Link>

              <button type="button" className="btn-oc-print" onClick={handlePrint} title="Print Invoice">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                <span>{isBn ? 'রসিদ প্রিন্ট' : 'Print Receipt'}</span>
              </button>

              <Link to={`/track-order?order=${order.order_number}`} className="btn-oc-track">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <span>{isBn ? 'ট্র্যাক' : 'Track'}</span>
              </Link>
            </div>

          </div>

          {/* RIGHT COLUMN: Order Items & Price Breakdown */}
          <div className="oc-col-receipt">
            <div className="oc-receipt-card">
              
              <div className="oc-receipt-header">
                <span className="oc-receipt-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  {t('orderConfirmation.orderItems', 'অর্ডার আইটেম')}
                </span>
                <span className="oc-items-count-badge">
                  {order.items?.length || 0} {isBn ? 'টি পণ্য' : 'items'}
                </span>
              </div>

              {/* Scrollable Items List */}
              <div className="oc-items-scroll-list">
                {order.items?.map((item, idx) => {
                  const itemPrice = Number(item.price || 0);
                  const itemQty = Number(item.quantity || 1);
                  const itemTotal = Number(item.subtotal || itemPrice * itemQty);
                  const imgSrc = getImageUrl(item.product_image);

                  return (
                    <div key={idx} className="oc-item-row">
                      <div className="oc-item-img-box">
                        <img
                          src={imgSrc}
                          alt={item.product_name || 'Product'}
                          className="oc-item-img"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder-product.jpg';
                          }}
                        />
                        <span className="oc-item-qty-badge">{itemQty}</span>
                      </div>

                      <div className="oc-item-info">
                        <h4 className="oc-item-title">{item.product_name}</h4>
                        <div className="oc-item-specs">
                          {item.selected_size && (
                            <span className="spec-pill">{isBn ? 'সাইজ' : 'Size'}: {item.selected_size}</span>
                          )}
                          {item.selected_color && (
                            <span className="spec-pill">{isBn ? 'কালার' : 'Color'}: {item.selected_color}</span>
                          )}
                          {item.selected_weight && (
                            <span className="spec-pill">{item.selected_weight}</span>
                          )}
                        </div>
                        <span className="oc-item-unit-rate">৳{itemPrice.toFixed(2)} × {itemQty}</span>
                      </div>

                      <div className="oc-item-price-box">
                        <span className="oc-item-total font-mono">৳{itemTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pricing Breakdown */}
              <div className="oc-bill-breakdown">
                <div className="oc-bill-row">
                  <span className="bill-label">{isBn ? 'সাবটোটাল' : 'Subtotal'}</span>
                  <span className="bill-val font-mono">৳{itemsSubtotal.toFixed(2)}</span>
                </div>

                <div className="oc-bill-row">
                  <span className="bill-label">{isBn ? 'ডেলিভারি চার্জ' : 'Shipping Fee'}</span>
                  <span className={`bill-val font-mono ${shippingCost === 0 ? 'text-free' : ''}`}>
                    {shippingCost === 0 ? (isBn ? 'ফ্রি (৳০)' : 'FREE') : `৳${shippingCost.toFixed(2)}`}
                  </span>
                </div>

                {discountAmount > 0 && (
                  <div className="oc-bill-row discount-row">
                    <span className="bill-label">{isBn ? 'কুপন ছাড়' : 'Discount'}</span>
                    <span className="bill-val font-mono text-discount">-৳{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="oc-bill-divider"></div>

                <div className="oc-bill-row grand-total-row">
                  <div className="grand-label-group">
                    <span className="grand-label">{t('orderConfirmation.totalAmount', 'সর্বমোট পরিমাণ')}</span>
                    <span className="grand-tag">{isBn ? 'ক্যাশ অন ডেলিভারি' : 'Cash on Delivery'}</span>
                  </div>
                  <span className="grand-amount font-mono">৳{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Support / Help Banner */}
              <div className="oc-support-footer">
                <span className="oc-support-icon">💬</span>
                <div className="oc-support-text">
                  <span className="oc-support-title">{isBn ? 'যেকোনো সহায়তায় কল করুন:' : 'Need any support? Call us:'}</span>
                  <div className="oc-support-links">
                    <a href={`tel:${supportPhone}`} className="oc-support-link">{supportPhone}</a>
                    <span className="sep">•</span>
                    <a 
                      href={`https://wa.me/${whatsappNumber.replace(/\+/g, '').replace(/[^0-9]/g, '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="oc-support-link whatsapp"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>


        </div>

      </div>
    </div>
  );
};

export default OrderConfirmation;
